import { EMPTY_LIST } from '../../../../constants/common'
import {
  APPLICANT_CREDIT_CHECK_BAND,
  APPLICANT_STAGE,
  getApplicantFieldValue,
} from '../../../RentalPortalApplicant'
import { PHASE_STRIP_ITEM_STATE } from '../../RentalPortalPhaseBar'
import {
  APPLICANT_COMPLIANCE_HANDOFF_LINE_KEY,
  APPLICANT_COMPLIANCE_HANDOFF_STATUS,
  APPLICANT_COMPLIANCE_HANDOFF_STATUS_KEY,
  APPLICANT_COMPLIANCE_HANDOFF_STATUS_TEXT_COLOR,
  APPLICANT_COMPLIANCE_NEUTRAL_TEXT_COLOR,
  APPLICANT_COMPLIANCE_NOT_STARTED_STATUS,
  APPLICANT_COMPLIANCE_SCREENING_STATUS_KEY,
  APPLICANT_COMPLIANCE_SCREENING_STATUS_TEXT_COLOR,
} from '../RentalPortalApplicantProfileCompliance'
import {
  APPLICANT_PHASE_STATE,
  getApplicantShortDate,
} from '../RentalPortalApplicantProfileHeaderInfoItems'

import {
  APPLICANT_ATTENTION_ACTION_ROW_KEY,
  APPLICANT_CHECK_KEY,
  APPLICANT_CHECK_LABEL_KEY,
  APPLICANT_CREDIT_CHECK_HINT_KEY,
  APPLICANT_CREDIT_CHECK_RESULT_ROLE_SUPPLEMENTARY,
  APPLICANT_CREDIT_CHECK_STATUS,
  APPLICANT_SANCTIONS_CLEARED_STATUSES,
  APPLICANT_STAGE_SCREENING_GROUP,
} from './RentalPortalApplicantProfileWidgetStageScreening.constants'

function getApplicantCreditCheckSatisfyingResult(creditCheck) {
  const providerKey = creditCheck?.readiness_satisfied_by

  return providerKey
    ? [creditCheck?.expected, ...(creditCheck?.supplementary ?? EMPTY_LIST)]
        .filter(Boolean)
        .find(result => result?.provider_key === providerKey)
    : null
}

function getIsApplicantCreditCheckBandInconclusive(creditCheck) {
  return (
    getApplicantFieldValue(creditCheck?.expected?.normalised_band) ===
    APPLICANT_CREDIT_CHECK_BAND.inconclusive
  )
}

function getApplicantCreditCheckHint({ creditCheck, t }) {
  const satisfyingResult = getApplicantCreditCheckSatisfyingResult(creditCheck)

  if (satisfyingResult) {
    return t(
      satisfyingResult.role === APPLICANT_CREDIT_CHECK_RESULT_ROLE_SUPPLEMENTARY
        ? APPLICANT_CREDIT_CHECK_HINT_KEY.metSupplementary
        : APPLICANT_CREDIT_CHECK_HINT_KEY.met,
      { provider: satisfyingResult.provider_display_name }
    )
  }

  switch (creditCheck?.status) {
    case APPLICANT_CREDIT_CHECK_STATUS.notRequested:
    case APPLICANT_CREDIT_CHECK_STATUS.consentPending:
      return t(APPLICANT_CREDIT_CHECK_HINT_KEY.awaitingConsent)
    case APPLICANT_CREDIT_CHECK_STATUS.consentGiven:
      return t(APPLICANT_CREDIT_CHECK_HINT_KEY.awaitingReport)
    case APPLICANT_CREDIT_CHECK_STATUS.requested:
      return t(APPLICANT_CREDIT_CHECK_HINT_KEY.requested, {
        date: getApplicantShortDate(creditCheck?.requested_at),
      })
    case APPLICANT_CREDIT_CHECK_STATUS.documentAttached:
      return t(APPLICANT_CREDIT_CHECK_HINT_KEY.verdictOpen)
    case APPLICANT_CREDIT_CHECK_STATUS.expired:
      return t(APPLICANT_CREDIT_CHECK_HINT_KEY.expired)
    case APPLICANT_CREDIT_CHECK_STATUS.recorded:
      return getIsApplicantCreditCheckBandInconclusive(creditCheck) &&
        creditCheck?.expected?.provider_display_name
        ? t(APPLICANT_CREDIT_CHECK_HINT_KEY.noData, {
            provider: creditCheck.expected.provider_display_name,
          })
        : null
    default:
      return null
  }
}

function getApplicantDecisionHint({ dateValue, nameKey, t, user }) {
  return user?.full_name && dateValue
    ? t(nameKey, {
        date: getApplicantShortDate(dateValue),
        name: user.full_name,
      })
    : null
}

function getIsApplicantSanctionsCleared(status) {
  return APPLICANT_SANCTIONS_CLEARED_STATUSES.includes(status)
}

function getApplicantSanctionsCheck({ screeningState, t }) {
  const status = screeningState ?? APPLICANT_COMPLIANCE_NOT_STARTED_STATUS

  return {
    hint: null,
    isDone: getIsApplicantSanctionsCleared(status),
    key: APPLICANT_CHECK_KEY.sanctions,
    statusText: t(
      APPLICANT_COMPLIANCE_SCREENING_STATUS_KEY[status] ??
        'RentalPortal.ApplicantProfileComplianceNotStartedKey'
    ),
    statusTextColor:
      APPLICANT_COMPLIANCE_SCREENING_STATUS_TEXT_COLOR[status] ??
      APPLICANT_COMPLIANCE_NEUTRAL_TEXT_COLOR,
  }
}

function getIsApplicantHandoffDelivered(status) {
  return status === APPLICANT_COMPLIANCE_HANDOFF_STATUS.delivered
}

function getApplicantHandoffCheck({ handoff, t }) {
  const status = handoff?.status ?? APPLICANT_COMPLIANCE_NOT_STARTED_STATUS
  const lineKey = APPLICANT_COMPLIANCE_HANDOFF_LINE_KEY[status]

  return {
    hint:
      lineKey && handoff?.updated_at
        ? t(lineKey, { date: getApplicantShortDate(handoff.updated_at) })
        : null,
    isDone: getIsApplicantHandoffDelivered(status),
    key: APPLICANT_CHECK_KEY.handoff,
    statusText: t(
      APPLICANT_COMPLIANCE_HANDOFF_STATUS_KEY[status] ??
        'RentalPortal.ApplicantProfileComplianceNotStartedKey'
    ),
    statusTextColor:
      APPLICANT_COMPLIANCE_HANDOFF_STATUS_TEXT_COLOR[status] ??
      APPLICANT_COMPLIANCE_NEUTRAL_TEXT_COLOR,
  }
}

export function getIsApplicantCurrentPhasePool(applicantData) {
  return (
    (applicantData.phase?.key ?? applicantData.stage) === APPLICANT_STAGE.pool
  )
}

export function getIsApplicantScreeningStarted(applicantData) {
  return Boolean(
    applicantData && !getIsApplicantCurrentPhasePool(applicantData)
  )
}

export function getIsApplicantPhaseStripItemDone(item) {
  return item.state === PHASE_STRIP_ITEM_STATE.done
}

export function getIsApplicantPhaseStripItemNeedsYou(item) {
  return item.state === PHASE_STRIP_ITEM_STATE.needsYou
}

export function getApplicantPhaseStripChecks({ isActive, phase }) {
  return (phase?.strip?.items ?? EMPTY_LIST)
    .filter(item => Boolean(item.label))
    .map(item => ({
      hint: item.meta,
      isDone: getIsApplicantPhaseStripItemDone(item),
      isNeedsYou: isActive && getIsApplicantPhaseStripItemNeedsYou(item),
      label: item.label,
    }))
}

export function getIsApplicantPoolPhase(phase) {
  return phase.key === APPLICANT_STAGE.pool
}

export function getIsApplicantPhaseActive(phase) {
  return phase?.state === APPLICANT_PHASE_STATE.active
}

export function getApplicantPoolChecks(applicantData) {
  const poolPhase = (applicantData?.phases ?? EMPTY_LIST).find(phase =>
    getIsApplicantPoolPhase(phase)
  )

  return getApplicantPhaseStripChecks({
    isActive:
      !applicantData?.phase?.is_terminal &&
      getIsApplicantPhaseActive(poolPhase),
    phase: poolPhase,
  })
}

export function getApplicantScreeningChecks({ applicantData, t }) {
  const creditCheck = applicantData?.credit_check
  const documents = applicantData?.documents
  const isDocumentsDone = Boolean(documents?.complete)
  const isConsentDone = Boolean(creditCheck?.consent?.given)

  return [
    {
      hint: isConsentDone
        ? null
        : t('RentalPortal.ApplicantCheckConsentOpenKey'),
      isDone: isConsentDone,
      key: APPLICANT_CHECK_KEY.consent,
    },
    {
      hint:
        documents?.required == null
          ? null
          : t('RentalPortal.ApplicantCheckDocumentsProgressKey', {
              required: documents.required,
              verified: documents.verified,
            }),
      isDone: isDocumentsDone,
      key: APPLICANT_CHECK_KEY.documents,
    },
    {
      hint: getApplicantCreditCheckHint({ creditCheck, t }),
      isDone: Boolean(creditCheck?.readiness_satisfied_by),
      key: APPLICANT_CHECK_KEY.creditCheck,
    },
    ...(applicantData?.policy?.sanctions_screening_required &&
    applicantData?.permissions?.can_view_audit
      ? [
          getApplicantSanctionsCheck({
            screeningState: applicantData.screening_state,
            t,
          }),
        ]
      : EMPTY_LIST),
  ].map(check => ({ ...check, label: t(APPLICANT_CHECK_LABEL_KEY[check.key]) }))
}

export function getApplicantDecisionChecks({ applicantData, t }) {
  const isSelected = Boolean(applicantData?.is_selected)
  const isApproved = Boolean(applicantData?.approved_by_user_id)

  return [
    {
      hint: isSelected
        ? getApplicantDecisionHint({
            dateValue: applicantData?.selected_at,
            nameKey: 'RentalPortal.ApplicantCheckSelectedByKey',
            t,
            user: applicantData?.selected_by,
          })
        : t('RentalPortal.ApplicantCheckSelectedOpenKey'),
      isDone: isSelected,
      key: APPLICANT_CHECK_KEY.selected,
    },
    ...(applicantData?.policy?.four_eyes_required
      ? [
          {
            hint: isApproved
              ? getApplicantDecisionHint({
                  dateValue: applicantData?.approved_at,
                  nameKey: 'RentalPortal.ApplicantCheckFourEyesByKey',
                  t,
                  user: applicantData?.approved_by,
                })
              : t('RentalPortal.ApplicantCheckFourEyesOpenKey'),
            isDone: isApproved,
            key: APPLICANT_CHECK_KEY.fourEyes,
          },
        ]
      : EMPTY_LIST),
    ...(applicantData?.policy?.erp_handoff_enabled
      ? [getApplicantHandoffCheck({ handoff: applicantData?.handoff, t })]
      : EMPTY_LIST),
  ].map(check => ({ ...check, label: t(APPLICANT_CHECK_LABEL_KEY[check.key]) }))
}

export function getApplicantScreeningCount(checks) {
  return {
    done: checks.filter(check => check.isDone).length,
    total: checks.length,
  }
}

export function getApplicantMarkedCheckKey({ applicantData, checkKeys }) {
  const rowKey =
    APPLICANT_ATTENTION_ACTION_ROW_KEY[
      applicantData?.attention?.primary_action?.key
    ]

  return rowKey && checkKeys.includes(rowKey) ? rowKey : null
}

export function toApplicantConnectedChecks(checks) {
  const firstOpenIndex = checks.findIndex(check => !check.isDone)

  return checks.map((check, index) => ({
    ...check,
    isLast: index === checks.length - 1,
    isLineDone: firstOpenIndex === -1 || index + 1 < firstOpenIndex,
  }))
}

export function getApplicantStageScreeningGroups({ applicantData, t }) {
  return [
    {
      checks: toApplicantConnectedChecks(
        getApplicantScreeningChecks({ applicantData, t })
      ),
      key: APPLICANT_STAGE_SCREENING_GROUP.screening,
    },
    {
      checks: toApplicantConnectedChecks(
        getApplicantDecisionChecks({ applicantData, t })
      ),
      key: APPLICANT_STAGE_SCREENING_GROUP.decision,
    },
  ]
}

export function getIsApplicantScreeningGroup(groupKey) {
  return groupKey === APPLICANT_STAGE_SCREENING_GROUP.screening
}
